import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { MainLayout } from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { 
  Plus, Play, Pause, Users, Clock, 
  Trash2, Edit2, Zap, RefreshCw,
  User, X, BarChart3, TrendingUp, Target, Info
} from 'lucide-react';
import { toast } from 'sonner';

// Trigger type labels
const TRIGGER_LABELS = {
  manual: 'Manual Enrollment',
  new_contact: 'New Contact Added',
  quote_sent: 'Quote Sent',
  contact_warm: 'Contact Marked Warm'
};

export default function SequencesPage() {
  const { authFetch, user } = useAuth();
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [activeEnrollments, setActiveEnrollments] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [performance, setPerformance] = useState(null);
  const [optimization, setOptimization] = useState(null);
  const [infoProfile, setInfoProfile] = useState(null);
  
  const isDirector = user?.role === 'director';

  // Form state for creating/editing sequences
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_type: 'manual',
    is_active: true,
    steps: [
      { step_number: 1, delay_days: 0, action_type: 'task', subject: '', template_content: '' }
    ]
  });

  useEffect(() => {
    loadSequences();
    loadActiveEnrollments();
    if (isDirector) loadPerformance();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSequences = async () => {
    try {
      const res = await authFetch('/sequences');
      if (res.ok) {
        const data = await res.json();
        setSequences(data.sequences || []);
      }
    } catch (err) {
      console.error('Failed to load sequences:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadActiveEnrollments = async () => {
    try {
      const res = await authFetch('/sequences/enrollments/active');
      if (res.ok) {
        const data = await res.json();
        setActiveEnrollments(data.enrollments || []);
      }
    } catch (err) {
      console.error('Failed to load enrollments:', err);
    }
  };

  const loadPerformance = async () => {
    try {
      const [perfRes, optRes] = await Promise.all([
        authFetch('/sequences/performance'),
        authFetch('/sequences/optimization')
      ]);
      if (perfRes.ok) {
        const data = await perfRes.json();
        setPerformance(data.performance || []);
      }
      if (optRes.ok) {
        const data = await optRes.json();
        setOptimization(data.optimization || []);
      }
    } catch (err) {
      console.error('Failed to load performance:', err);
    }
  };

  const handleCreateSequence = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a sequence name');
      return;
    }
    if (formData.steps.length === 0) {
      toast.error('Please add at least one step');
      return;
    }
    
    // Validate steps
    for (let i = 0; i < formData.steps.length; i++) {
      const step = formData.steps[i];
      if (!step.subject.trim() || !step.template_content.trim()) {
        toast.error(`Please complete step ${i + 1}`);
        return;
      }
    }

    try {
      const res = await authFetch('/sequences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success('Sequence created successfully');
        setShowCreateModal(false);
        resetForm();
        loadSequences();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create sequence');
      }
    } catch (err) {
      toast.error('Failed to create sequence');
    }
  };

  const handleUpdateSequence = async () => {
    if (!selectedSequence) return;
    
    try {
      const res = await authFetch(`/sequences/${selectedSequence.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success('Sequence updated successfully');
        setShowCreateModal(false);
        resetForm();
        loadSequences();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update sequence');
      }
    } catch (err) {
      toast.error('Failed to update sequence');
    }
  };

  const handleDeleteSequence = async (sequenceId) => {
    if (!window.confirm('Are you sure you want to delete this sequence? All enrollments will be removed.')) return;
    
    try {
      const res = await authFetch(`/sequences/${sequenceId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Sequence deleted');
        loadSequences();
        loadActiveEnrollments();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete sequence');
      }
    } catch (err) {
      toast.error('Failed to delete sequence');
    }
  };

  const handleToggleActive = async (sequence) => {
    try {
      const res = await authFetch(`/sequences/${sequence.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !sequence.is_active })
      });
      
      if (res.ok) {
        toast.success(sequence.is_active ? 'Sequence paused' : 'Sequence activated');
        loadSequences();
      }
    } catch (err) {
      toast.error('Failed to update sequence');
    }
  };

  const handleProcessDueSteps = async () => {
    setProcessing(true);
    try {
      const res = await authFetch('/sequences/process-due-steps', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.tasks_created} task(s) created, ${data.enrollments_completed} completed`);
        loadActiveEnrollments();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to process sequences');
      }
    } catch (err) {
      toast.error('Failed to process sequences');
    } finally {
      setProcessing(false);
    }
  };

  const handleUnenroll = async (enrollment) => {
    if (!window.confirm(`Remove ${enrollment.contact_name} from this sequence?`)) return;
    
    try {
      const res = await authFetch(`/sequences/${enrollment.sequence_id}/unenroll/${enrollment.contact_id}`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Contact removed from sequence');
        loadActiveEnrollments();
        if (selectedSequence) {
          loadSequenceDetail(selectedSequence.id);
        }
      }
    } catch (err) {
      toast.error('Failed to remove contact');
    }
  };

  const loadSequenceDetail = async (sequenceId) => {
    try {
      const res = await authFetch(`/sequences/${sequenceId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedSequence({ ...data.sequence, enrollments: data.enrollments });
        setShowDetailModal(true);
      }
    } catch (err) {
      toast.error('Failed to load sequence details');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      trigger_type: 'manual',
      is_active: true,
      steps: [
        { step_number: 1, delay_days: 0, action_type: 'task', subject: '', template_content: '' }
      ]
    });
    setSelectedSequence(null);
  };

  const openEditModal = (sequence) => {
    setFormData({
      name: sequence.name,
      description: sequence.description || '',
      trigger_type: sequence.trigger_type,
      is_active: sequence.is_active,
      steps: sequence.steps || []
    });
    setSelectedSequence(sequence);
    setShowCreateModal(true);
  };

  const addStep = () => {
    const newStep = {
      step_number: formData.steps.length + 1,
      delay_days: formData.steps.length === 0 ? 0 : 3,
      action_type: 'task',
      subject: '',
      template_content: ''
    };
    setFormData({ ...formData, steps: [...formData.steps, newStep] });
  };

  const removeStep = (index) => {
    const newSteps = formData.steps.filter((_, i) => i !== index).map((step, i) => ({
      ...step,
      step_number: i + 1
    }));
    setFormData({ ...formData, steps: newSteps });
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...formData.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setFormData({ ...formData, steps: newSteps });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-full overflow-x-hidden p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Follow-up Sequences</h1>
            <p className="text-slate-500 mt-1">Automate your follow-up workflows</p>
          </div>
          <div className="flex items-center gap-2">
            {isDirector && (
              <>
                <Button
                  onClick={handleProcessDueSteps}
                  variant="outline"
                  className="rounded-xl"
                  disabled={processing}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${processing ? 'animate-spin' : ''}`} />
                  Process Due Steps
                </Button>
                <Button
                  onClick={() => {
                    resetForm();
                    setShowCreateModal(true);
                  }}
                  className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Sequence
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{sequences.length}</p>
                <p className="text-xs text-slate-500">Total Sequences</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                <Play className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{sequences.filter(s => s.is_active).length}</p>
                <p className="text-xs text-slate-500">Active</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{activeEnrollments.length}</p>
                <p className="text-xs text-slate-500">Contacts Enrolled</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">
                  {activeEnrollments.filter(e => new Date(e.next_step_due) <= new Date()).length}
                </p>
                <p className="text-xs text-slate-500">Steps Due</p>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Dashboard — Director only */}
        {isDirector && performance && performance.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#1e3a5f]" />
                Scenario Performance
              </h2>
              <Button size="sm" variant="ghost" onClick={loadPerformance} className="text-slate-500">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4">
              {performance.map((p) => (
                <div
                  key={p.profile.id}
                  className="p-4 rounded-xl border border-slate-100"
                  style={{ borderLeftWidth: '4px', borderLeftColor: p.profile.color }}
                  data-testid={`perf-${p.profile.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-sm text-slate-800">{p.profile.label}</h3>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); setInfoProfile(p.profile); }}
                        className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                        data-testid={`info-${p.profile.id}`}
                      >
                        <Info className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        W{p.profile.warmth_score}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <p className="text-lg font-bold text-[#1e3a5f]">{p.total_enrollments}</p>
                      <p className="text-[10px] text-slate-500">Enrolled</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <p className="text-lg font-bold" style={{ color: p.profile.color }}>{p.engagement_rate}%</p>
                      <p className="text-[10px] text-slate-500">Engaged</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <p className="text-lg font-bold text-emerald-600">{p.meeting_conversions}</p>
                      <p className="text-[10px] text-slate-500">Meetings</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <p className="text-lg font-bold text-amber-600">{p.avg_days_to_engagement ?? '—'}</p>
                      <p className="text-[10px] text-slate-500">Avg Days</p>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-1">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{p.active} active</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{p.completed} done</span>
                    {p.stopped > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{p.stopped} stopped</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Optimization Insights */}
        {isDirector && optimization && optimization.some(o => o.insights?.length > 0) && (
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                Optimization Insights
              </h2>
            </div>
            <div className="p-4 space-y-4">
              {optimization.filter(o => o.insights?.length > 0).map((opt) => (
                <div key={opt.sequence_name} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-slate-800">{opt.sequence_name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{opt.total_enrollments} enrolled</span>
                  </div>
                  {opt.insights.map((insight, idx) => {
                    const styles = {
                      warning: 'bg-amber-50 border-amber-200 text-amber-800',
                      success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                      insight: 'bg-blue-50 border-blue-200 text-blue-800',
                      info: 'bg-slate-50 border-slate-200 text-slate-700',
                      optimize: 'bg-violet-50 border-violet-200 text-violet-800',
                    };
                    return (
                      <div key={idx} className={`p-3 rounded-lg border text-sm ${styles[insight.type] || styles.info}`}>
                        {insight.message}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sequences List */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Your Sequences</h2>
          </div>
          
          {sequences.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-slate-300" />
              </div>
              <p className="text-slate-500">No sequences yet</p>
              {isDirector && (
                <Button
                  onClick={() => setShowCreateModal(true)}
                  variant="outline"
                  className="mt-4 rounded-xl"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Sequence
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {sequences.map(sequence => (
                <div
                  key={sequence.id}
                  className="p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => loadSequenceDetail(sequence.id)}
                    >
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-slate-800">{sequence.name}</h3>
                        {sequence.is_active ? (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                            Active
                          </span>
                        ) : (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                            Paused
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{sequence.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {sequence.steps?.length || 0} steps
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {sequence.active_enrollments || 0} enrolled
                        </span>
                        <span>{TRIGGER_LABELS[sequence.trigger_type] || sequence.trigger_type}</span>
                      </div>
                    </div>
                    
                    {isDirector && (
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleActive(sequence);
                          }}
                          variant="ghost"
                          size="sm"
                          className="rounded-lg"
                        >
                          {sequence.is_active ? (
                            <Pause className="w-4 h-4 text-amber-500" />
                          ) : (
                            <Play className="w-4 h-4 text-green-500" />
                          )}
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(sequence);
                          }}
                          variant="ghost"
                          size="sm"
                          className="rounded-lg"
                        >
                          <Edit2 className="w-4 h-4 text-slate-400" />
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSequence(sequence.id);
                          }}
                          variant="ghost"
                          size="sm"
                          className="rounded-lg"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active Enrollments */}
        {activeEnrollments.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-semibold text-slate-800">Active Enrollments ({activeEnrollments.length})</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {activeEnrollments.map(enrollment => (
                <div key={enrollment.id} className="p-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-slate-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 truncate">{enrollment.contact_name}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {enrollment.sequence_name} — Step {enrollment.current_step + 1}/{enrollment.total_steps}
                      </p>
                      {enrollment.company_name && (
                        <p className="text-[10px] text-slate-400 truncate">{enrollment.company_name}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="text-right text-xs text-slate-400 hidden sm:block">
                      <p>Next:</p>
                      <p className={new Date(enrollment.next_step_due) <= new Date() ? 'text-amber-600 font-medium' : ''}>
                        {new Date(enrollment.next_step_due).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                    <Button
                      onClick={async () => {
                        try {
                          const res = await authFetch(`/sequences/enrollments/${enrollment.id}/advance`, { method: 'POST' });
                          if (res.ok) {
                            const data = await res.json();
                            toast.success(data.message);
                            loadActiveEnrollments();
                            loadSequences();
                          } else {
                            const err = await res.json();
                            toast.error(err.detail || 'Failed to advance');
                          }
                        } catch { toast.error('Failed to advance step'); }
                      }}
                      variant="outline"
                      size="sm"
                      className="rounded-lg text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                      data-testid={`advance-${enrollment.id}`}
                    >
                      <Play className="w-3 h-3 mr-1" />
                      Next
                    </Button>
                    <Button
                      onClick={() => handleUnenroll(enrollment)}
                      variant="ghost"
                      size="sm"
                      className="rounded-lg"
                    >
                      <X className="w-4 h-4 text-slate-400" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create/Edit Sequence Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedSequence ? 'Edit Sequence' : 'Create Sequence'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Sequence Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., New Lead Nurture"
                    className="mt-1"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this sequence..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
                
                <div>
                  <Label>Trigger</Label>
                  <Select
                    value={formData.trigger_type}
                    onValueChange={(value) => setFormData({ ...formData, trigger_type: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual Enrollment</SelectItem>
                      <SelectItem value="new_contact">New Contact Added</SelectItem>
                      <SelectItem value="quote_sent">Quote Sent</SelectItem>
                      <SelectItem value="contact_warm">Contact Marked Warm</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label>Active</Label>
                </div>
              </div>
              
              {/* Steps */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base">Steps</Label>
                  <Button
                    onClick={addStep}
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Step
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {formData.steps.map((step, index) => (
                    <div key={index} className="bg-slate-50 rounded-xl p-4 relative">
                      <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      
                      <div className="pl-6 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div>
                              <Label className="text-xs text-slate-500">Delay (days)</Label>
                              <Input
                                type="number"
                                min="0"
                                value={step.delay_days}
                                onChange={(e) => updateStep(index, 'delay_days', parseInt(e.target.value) || 0)}
                                className="w-20 mt-1"
                              />
                            </div>
                            <div className="text-xs text-slate-400 pt-5">
                              {index === 0 ? 'after enrollment' : 'after previous step'}
                            </div>
                          </div>
                          
                          {formData.steps.length > 1 && (
                            <Button
                              onClick={() => removeStep(index)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        
                        <div>
                          <Label className="text-xs text-slate-500">Task Subject</Label>
                          <Input
                            value={step.subject}
                            onChange={(e) => updateStep(index, 'subject', e.target.value)}
                            placeholder="e.g., Follow-up Call"
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-xs text-slate-500">Task Instructions</Label>
                          <Textarea
                            value={step.template_content}
                            onChange={(e) => updateStep(index, 'template_content', e.target.value)}
                            placeholder="What should the team member do in this step?"
                            className="mt-1"
                            rows={3}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                variant="outline"
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={selectedSequence ? handleUpdateSequence : handleCreateSequence}
                className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl"
              >
                {selectedSequence ? 'Save Changes' : 'Create Sequence'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Sequence Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedSequence?.name}</DialogTitle>
            </DialogHeader>
            
            {selectedSequence && (
              <div className="space-y-6 py-4">
                <p className="text-slate-500">{selectedSequence.description}</p>
                
                {/* Steps Timeline */}
                <div>
                  <h3 className="font-medium text-slate-800 mb-3">Sequence Steps</h3>
                  <div className="relative pl-8">
                    <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-slate-200" />
                    
                    {selectedSequence.steps?.map((step, index) => (
                      <div key={index} className="relative mb-4 last:mb-0">
                        <div className="absolute -left-5 w-6 h-6 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center text-xs font-bold">
                          {index + 1}
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3 ml-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-500">Day {step.delay_days}</span>
                          </div>
                          <p className="font-medium text-slate-800 mt-1">{step.subject}</p>
                          <p className="text-sm text-slate-500 mt-1">{step.template_content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Enrolled Contacts */}
                <div>
                  <h3 className="font-medium text-slate-800 mb-3">
                    Enrolled Contacts ({selectedSequence.enrollments?.length || 0})
                  </h3>
                  
                  {selectedSequence.enrollments?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedSequence.enrollments.map(enrollment => (
                        <div
                          key={enrollment.id}
                          className="flex items-center justify-between bg-slate-50 rounded-lg p-3"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center border">
                              <User className="w-4 h-4 text-slate-400" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-800 text-sm">{enrollment.contact_name}</p>
                              <p className="text-xs text-slate-500">{enrollment.company_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-xs text-slate-500">
                                Step {enrollment.current_step + 1}/{selectedSequence.steps?.length}
                              </p>
                              <p className={`text-xs ${enrollment.status === 'active' ? 'text-green-600' : 'text-slate-400'}`}>
                                {enrollment.status}
                              </p>
                            </div>
                            {enrollment.status === 'active' && (
                              <Button
                                onClick={() => handleUnenroll(enrollment)}
                                variant="ghost"
                                size="sm"
                              >
                                <X className="w-4 h-4 text-slate-400" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-sm">No contacts enrolled yet</p>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Scenario Info Dialog */}
        <Dialog open={!!infoProfile} onOpenChange={() => setInfoProfile(null)}>
          <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto">
            {infoProfile && (() => {
              const seq = sequences.find(s => s.scenario_profile === infoProfile.id);
              const objectives = {
                referred_prospect: { goal: 'Rapid conversion to call', intensity: 'High', duration: '14 days', tone: 'Direct and confident', stopCondition: 'Meeting booked or positive engagement logged' },
                networking_contact: { goal: 'Relationship reinforcement', intensity: 'Low to moderate', duration: '35 days', tone: 'Personal and relational', stopCondition: 'Meeting booked or ongoing engagement logged' },
                new_linkedin_connection: { goal: 'Warm relationship building', intensity: 'Moderate', duration: '30 days', tone: 'Insight-led, conversational', stopCondition: 'Meeting logged or two-way communication recorded' },
                cold_prospect: { goal: 'Multi-channel exposure', intensity: 'Structured persistence', duration: '28 days', tone: 'Professional, concise', stopCondition: 'Any response logged' },
                dormant_reengagement: { goal: 'Faster signal, quicker qualification', intensity: 'Gradual escalation', duration: '21 days', tone: 'Direct, value-led', stopCondition: 'Meeting booked, call connected, positive email response, or status clarified' },
              };
              const obj = objectives[infoProfile.id] || {};
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                      <div className="w-3 h-8 rounded-full" style={{ backgroundColor: infoProfile.color }} />
                      <div>
                        <span className="text-lg">{infoProfile.label}</span>
                        <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Warmth {infoProfile.warmth_score}/4</span>
                      </div>
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-2">
                    {/* Strategy overview */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-slate-50 rounded-xl">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide">Objective</p>
                        <p className="text-sm font-medium text-slate-800">{obj.goal}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-xl">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide">Intensity</p>
                        <p className="text-sm font-medium text-slate-800">{obj.intensity}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-xl">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide">Duration</p>
                        <p className="text-sm font-medium text-slate-800">{obj.duration}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-xl">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide">Tone</p>
                        <p className="text-sm font-medium text-slate-800">{obj.tone}</p>
                      </div>
                    </div>

                    {/* Sequence steps */}
                    {seq && seq.steps && (
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Sequence Steps</p>
                        <div className="space-y-0">
                          {seq.steps.map((step, idx) => (
                            <div key={idx} className="flex gap-3">
                              {/* Timeline */}
                              <div className="flex flex-col items-center">
                                <div
                                  className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                  style={{ backgroundColor: infoProfile.color }}
                                >
                                  {idx + 1}
                                </div>
                                {idx < seq.steps.length - 1 && (
                                  <div className="w-0.5 flex-1 min-h-[24px]" style={{ backgroundColor: `${infoProfile.color}30` }} />
                                )}
                              </div>
                              {/* Content */}
                              <div className="pb-4 flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className="text-xs font-bold text-slate-400">Day {step.delay_days}</span>
                                </div>
                                <p className="font-medium text-sm text-slate-800">{step.subject}</p>
                                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{step.template_content}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Auto-stop condition */}
                    <div className="p-3 rounded-xl border border-amber-200 bg-amber-50">
                      <p className="text-xs font-semibold text-amber-700 mb-1">Auto-Stop Condition</p>
                      <p className="text-sm text-amber-800">{obj.stopCondition}</p>
                    </div>
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
